import { useContext, useEffect } from 'react';
import { useRouter } from 'next/router';
import DocsContext from 'src/contexts/docs';
import useUser from 'src/contexts/docs/useUser';
import ErrorPage from '../ErrorPage';
import GatedPage from '../GatedPage';
import { ArticleMetadata } from '../types';
import LoggedInWrapper from '../LoggedInWrapper';
import Link from 'next/link';

interface Props {
  children: JSX.Element;
  metadata: ArticleMetadata;
}

export const FlagWrapper = (props: Props) => {
  const router = useRouter();
  const { getFlagValue, isLoggedIn } = useUser();
  const { user } = useContext(DocsContext);
  const flagNotPresent = props.metadata.flag === undefined;
  const isIndexBuild = process.env.INDEX_GATED_DOCS === 'true';
  const flagErrorRedirect = props.metadata.flagErrorRedirect;
  const flagValue = getFlagValue(props.metadata.flag);
  const shouldUseRedirect =
    !flagNotPresent && flagErrorRedirect != null && !isIndexBuild;
  const shouldRedirect =
    shouldUseRedirect &&
    isLoggedIn !== null &&
    (isLoggedIn === false || flagValue !== true);

  useEffect(() => {
    if (shouldRedirect && flagErrorRedirect != null) {
      void router.replace(flagErrorRedirect);
    }
  }, [flagErrorRedirect, router, shouldRedirect]);

  // When building the gated-docs search index we need the real content baked into
  // the static HTML instead of the logged-out error page. This build output is used
  // only for index extraction (scripts/index-protect-docs.js) and is never deployed.
  if (flagNotPresent || isIndexBuild) {
    return props.children;
  }

  if (shouldUseRedirect) {
    return isLoggedIn === true && flagValue === true ? props.children : null;
  }

  const errorPage = props.metadata.flagGatedPage ? (
    <GatedPage
      isLoggedIn={isLoggedIn}
      section={props.metadata.section}
      title={props.metadata.title}
      body={props.metadata.flagErrorBody}
      cta={props.metadata.flagErrorCta}
      teamName={user?.team?.company}
    />
  ) : (
    <ErrorPage
      header='This page is not available'
      subheader={
        isLoggedIn ? (
          'You do not have access to this page'
        ) : (
          <Link href='https://dashboard.plaid.com/signin?redirect=docs'>
            Log in to view this page
          </Link>
        )
      }
      body={
        props.metadata.flagErrorBody ||
        'If you believe this is a mistake, please contact your Account Manager'
      }
    />
  );
  return (
    <LoggedInWrapper
      loggedInComponent={flagValue === true ? props.children : errorPage}
      loggedOutComponent={errorPage}
    />
  );
};
